import { useElapsedTime } from '@/hooks/use-elapsed-time';
import { useLatestConversationId } from '@/hooks/use-latest-conversation-id';
import { useUpdateConversationTimeSpent } from '@/hooks/use-update-conversation-time-spent';
import { logger } from '@/libs/logger';
import {
    ConversationResponse,
    ConversationRequest,
    ConversationConversationTypeEnum,
} from '@calm/evexia-web-sdk';
import isSSR from '@calm/web-common/dist/isSSR';
import { useSaveAiConversationData } from '@rhg/hooks/use-save-ai-conversation';
import { ReactNode, createContext, useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { JournalingState } from './types';

export const JournalingContext = createContext<JournalingState>(undefined!);

export const JOURNALING_CONVERSATION_KEY = 'journalingConversationData';
export const JOURNALING_PROMPT_RESPONSES_KEY = 'journalingPromptResponses';
export const JOURNALING_PROMPT_INPUTS_KEY = 'journalingPromptInputs';
export const JOURNALING_ID_KEY = 'journalingId';

// Doesnt make sense to track time if user spends less than 5 seconds on a step, as they likely didnt engage with the content
const MIN_TIME_SPENT_SECONDS = 5;

export const hydrateConversationData = (): ConversationResponse | undefined => {
    if (isSSR()) return undefined;
    const stored = sessionStorage.getItem(JOURNALING_CONVERSATION_KEY);
    if (!stored) return undefined;
    try {
        return JSON.parse(stored) as ConversationResponse;
    } catch {
        return undefined;
    }
};

const persistConversationData = (data: ConversationResponse | undefined) => {
    if (isSSR()) return;
    if (data) {
        sessionStorage.setItem(
            JOURNALING_CONVERSATION_KEY,
            JSON.stringify(data)
        );
    } else {
        sessionStorage.removeItem(JOURNALING_CONVERSATION_KEY);
    }
};

const hydratePromptResponses = (): Record<string, ConversationResponse> => {
    if (isSSR()) return {};
    const stored = sessionStorage.getItem(JOURNALING_PROMPT_RESPONSES_KEY);
    if (!stored) return {};
    try {
        return JSON.parse(stored) as Record<string, ConversationResponse>;
    } catch {
        // Drop the corrupted value so we don't re-parse it on every mount.
        sessionStorage.removeItem(JOURNALING_PROMPT_RESPONSES_KEY);
        return {};
    }
};

const persistPromptResponses = (
    responses: Record<string, ConversationResponse>
) => {
    if (isSSR()) return;
    if (Object.keys(responses).length) {
        sessionStorage.setItem(
            JOURNALING_PROMPT_RESPONSES_KEY,
            JSON.stringify(responses)
        );
    } else {
        sessionStorage.removeItem(JOURNALING_PROMPT_RESPONSES_KEY);
    }
};

// The user's own prompt answers keyed by the prompt step they were typed on.
// Persisted so back-navigation (notably from a garbage echo back) can re-seed
// the text field with what the user previously wrote, letting them edit it
// instead of retyping from scratch.
const hydratePromptInputs = (): Record<string, string> => {
    if (isSSR()) return {};
    const stored = sessionStorage.getItem(JOURNALING_PROMPT_INPUTS_KEY);
    if (!stored) return {};
    try {
        return JSON.parse(stored) as Record<string, string>;
    } catch {
        // Drop the corrupted value so we don't re-parse it on every mount.
        sessionStorage.removeItem(JOURNALING_PROMPT_INPUTS_KEY);
        return {};
    }
};

const persistPromptInputs = (inputs: Record<string, string>) => {
    if (isSSR()) return;
    if (Object.keys(inputs).length) {
        sessionStorage.setItem(
            JOURNALING_PROMPT_INPUTS_KEY,
            JSON.stringify(inputs)
        );
    } else {
        sessionStorage.removeItem(JOURNALING_PROMPT_INPUTS_KEY);
    }
};

const hydrateJournalingId = (): string => {
    if (isSSR()) return '';
    const stored = sessionStorage.getItem(JOURNALING_ID_KEY);
    if (stored) return stored;
    const generated = uuid();
    sessionStorage.setItem(JOURNALING_ID_KEY, generated);
    return generated;
};

const persistJournalingId = (id: string) => {
    if (isSSR()) return;
    sessionStorage.setItem(JOURNALING_ID_KEY, id);
};

// Begins a fresh journal entry by rotating the sessionStorage-backed
// journalingId and clearing any cached conversation data. Safe to call from
// outside the JournalingProvider tree — the provider on the journal-entry page
// hydrates from sessionStorage on mount. Callers that need the id at render time
// (e.g. to attach it to a click event) can pass it in so the persisted id
// matches the one already sent to analytics.
export const startNewJournalEntry = (journalingId: string = uuid()): string => {
    persistJournalingId(journalingId);
    persistConversationData(undefined);
    persistPromptResponses({});
    persistPromptInputs({});
    return journalingId;
};

export function JournalingProvider({ children }: { children: ReactNode }) {
    const [conversationData, setConversationDataState] = useState<
        ConversationResponse | undefined
    >(hydrateConversationData);

    const setConversationData = useCallback(
        (data: ConversationResponse | undefined) => {
            persistConversationData(data);
            setConversationDataState(data);
        },
        []
    );

    const [journalingId] = useState<string>(hydrateJournalingId);

    // Echo-back responses keyed by the prompt step that produced them. Garbage
    // responses let the user navigate back across steps repeatedly, so the
    // latest response alone is not enough to re-render a prior echo back.
    const [promptResponses, setPromptResponsesState] = useState<
        Record<string, ConversationResponse>
    >(hydratePromptResponses);

    const setPromptResponse = useCallback(
        (promptStepId: string, data: ConversationResponse) => {
            setPromptResponsesState((prev) => {
                const next = { ...prev, [promptStepId]: data };
                persistPromptResponses(next);
                return next;
            });
        },
        []
    );

    // The user's own answers keyed by prompt step. Kept separately from the
    // AI responses above so back-navigation can restore the editable text the
    // user typed for a step (e.g. after a garbage echo back).
    const [promptInputs, setPromptInputsState] =
        useState<Record<string, string>>(hydratePromptInputs);

    const setPromptInput = useCallback((promptStepId: string, text: string) => {
        setPromptInputsState((prev) => {
            const next = { ...prev, [promptStepId]: text };
            persistPromptInputs(next);
            return next;
        });
    }, []);

    const clearConversationResponse = useCallback((promptStepId?: string) => {
        setConversationDataState((prev) => {
            if (!prev) return prev;
            const next = { ...prev, response: '' };
            persistConversationData(next);
            return next;
        });
        // Drop the step's cached echo back too, so a failed re-submit
        // can't surface the stale response on back-navigation.
        if (promptStepId) {
            setPromptResponsesState((prev) => {
                if (!(promptStepId in prev)) return prev;
                const next = { ...prev };
                delete next[promptStepId];
                persistPromptResponses(next);
                return next;
            });
        }
    }, []);

    const [lastPromptStep, setLastPromptStep] = useState<string | undefined>(
        undefined
    );

    const [isContinuingNextStep, setIsContinuingNextStep] = useState(false);

    const {
        saveAiConversationDataAsync,
        isPending: isSaveConversationPending,
    } = useSaveAiConversationData();

    const { getElapsedSeconds, setElapsedTimeStart } = useElapsedTime();
    const { updateConversationTimeSpent } = useUpdateConversationTimeSpent();

    const {
        conversationId: fetchedConversationId,
        isLoading: isConversationIdLoading,
    } = useLatestConversationId({
        conversationType: ConversationConversationTypeEnum.Journaling,
    });

    async function saveAiConversation(
        conversationRequest: ConversationRequest,
        conversationType?: ConversationConversationTypeEnum
    ) {
        let saved: ConversationResponse | undefined;
        await saveAiConversationDataAsync({
            conversationRequest,
            conversationType:
                conversationType || ConversationConversationTypeEnum.Journaling,
            onSave: (responseData) => {
                setConversationData(responseData);
                saved = responseData;
                return Promise.resolve();
            },
        });
        return saved;
    }

    const conversationId = conversationData?.conversationId;
    const cachedConvoId = conversationId ?? fetchedConversationId;

    // Submit elapsed time against the conversation the current session actually
    // created. Falls back to conversationData (never fetchedConversationId) so we
    // never attribute time to a cached previous conversation. Callers completing
    // the first prompt pass the freshly-created id explicitly, since the context
    // state has not updated yet within the same tick.
    const submitTimeSpent = useCallback(
        (conversationIdOverride?: string) => {
            const targetId =
                conversationIdOverride ?? conversationData?.conversationId;
            if (!targetId) return;
            const elapsedSeconds = getElapsedSeconds();
            if (elapsedSeconds > MIN_TIME_SPENT_SECONDS) {
                updateConversationTimeSpent(
                    {
                        conversationId: targetId,
                        additionalTimeSpentSeconds: elapsedSeconds,
                    },
                    {
                        onError: (error) => {
                            logger.error(
                                'Error updating journaling conversation time spent',
                                { conversationId: targetId, elapsedSeconds },
                                error
                            );
                        },
                    }
                );
            }
        },
        [
            conversationData?.conversationId,
            getElapsedSeconds,
            updateConversationTimeSpent,
        ]
    );

    return (
        <JournalingContext.Provider
            value={{
                conversationData,
                setConversationData,
                promptResponses,
                setPromptResponse,
                promptInputs,
                setPromptInput,
                clearConversationResponse,
                saveAiConversation,
                isSaveConversationPending,
                conversationId,
                cachedConvoId,
                isConversationIdLoading,
                journalingId,
                lastPromptStep,
                setLastPromptStep,
                submitTimeSpent,
                getElapsedSeconds,
                setElapsedTimeStart,
                isContinuingNextStep,
                setIsContinuingNextStep,
            }}
        >
            {children}
        </JournalingContext.Provider>
    );
}
